import { useEffect, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthApiService } from '../services/auth-api.service';
import type { InviteInfo } from '../services/auth-api.service';
import { ApiError } from '../services/http.client';
import { stageLogin } from '../services/staged-login';
import JoinOtpStep from './JoinOtpStep';
import {
  EMPTY_FORM, PASSWORD_MIN_LENGTH, getCodeFromSearch, mapJoinError, validateJoinForm,
} from './join.logic';
import type { JoinForm } from './join.logic';
import './LandingPage.css'; // same tokens, logo and buttons as didi365.eu

type Stage =
  | { kind: 'loading' }
  | { kind: 'blocked'; messageKey: string }
  | { kind: 'form'; info: InviteInfo; busy: boolean }
  | { kind: 'otp'; info: InviteInfo; busy: boolean }
  | { kind: 'done' };

/**
 * /join?code=XXXX-XXXX — self-service signup with an invite code.
 * Step 1: details → we mail a one-time code. Step 2: type the code → account
 * is created, a one-shot password login is staged and the page reloads into
 * /dashboard where KeycloakService.init() completes it. No AuthContext here.
 */
export default function JoinPage() {
  const { t } = useTranslation();
  const code = getCodeFromSearch(window.location.search);
  const [stage, setStage] = useState<Stage>({ kind: 'loading' });
  const [form, setForm] = useState<JoinForm>(EMPTY_FORM);
  const [badFields, setBadFields] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) { setStage({ kind: 'blocked', messageKey: 'join.noCode' }); return; }
    AuthApiService.getInviteInfo(code)
      .then((info) => setStage({ kind: 'form', info, busy: false }))
      .catch((err) => setStage({ kind: 'blocked', messageKey: mapJoinError(toJoinError(err)) }));
  }, [code]);

  const email = () => form.email.trim().toLowerCase();

  /** Step 1 → mail the code. */
  const onDetails = async (e: FormEvent) => {
    e.preventDefault();
    if (stage.kind !== 'form' || !code) return;
    const bad = validateJoinForm(form);
    setBadFields(bad); setError(null);
    if (bad.length > 0) return;
    setStage({ kind: 'form', info: stage.info, busy: true });
    try {
      await AuthApiService.sendInviteOtp(code, email());
      setStage({ kind: 'otp', info: stage.info, busy: false });
    } catch (err) {
      const j = toJoinError(err);
      setError(mapJoinError(j));
      if (j.details?.fields) setBadFields(j.details.fields);
      setStage({ kind: 'form', info: stage.info, busy: false });
    }
  };

  /** Step 2 → create the account with the mailed code. */
  const onOtp = async (otp: string) => {
    if (stage.kind !== 'otp' || !code) return;
    setError(null);
    setStage({ kind: 'otp', info: stage.info, busy: true });
    try {
      await AuthApiService.joinWithInvite(code, {
        email: email(), password: form.password, otp,
        firstName: form.firstName.trim(), lastName: form.lastName.trim(), acceptTerms: true,
      });
      stageLogin(email(), form.password);
      setStage({ kind: 'done' });
      window.location.replace('/dashboard'); // replace: Back must not restore the form/done state
    } catch (err) {
      const j = toJoinError(err);
      // a dead code or a taken email sends the visitor back to step 1 with the message
      const backToForm = j.status === 409 || j.status === 410 || j.status === 404;
      setError(mapJoinError(j));
      setStage({ kind: backToForm ? 'form' : 'otp', info: stage.info, busy: false });
    }
  };

  const onResend = async () => {
    if (stage.kind !== 'otp' || !code) return;
    setError(null);
    try { await AuthApiService.sendInviteOtp(code, email()); }
    catch (err) { setError(mapJoinError(toJoinError(err))); }
  };

  const set = (k: keyof JoinForm) => (v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));
  const invalid = (k: keyof JoinForm) => badFields.includes(k);

  return (
    <div className="landing-page" style={S.page}>
      <div style={S.card}>
        <a href="/" className="nav-logo" style={{ flex: 'none', textDecoration: 'none' }}>
          <span className="logo-text">didi</span>
        </a>
        {stage.kind === 'loading' && <p style={S.muted}>{t('join.loading')}</p>}

        {stage.kind === 'blocked' && (
          <>
            <h1 style={S.title}>{t('join.blockedTitle')}</h1>
            <p style={S.muted}>{t(stage.messageKey)}</p>
            <a href="/" style={S.link}>{t('join.goHome')}</a>
          </>
        )}

        {stage.kind === 'done' && <p style={S.muted}>{t('join.loggingIn')}</p>}

        {stage.kind === 'otp' && (
          <JoinOtpStep
            email={email()} busy={stage.busy} error={error} styles={S}
            onSubmit={onOtp} onResend={onResend}
            onChangeEmail={() => { setError(null); setStage({ kind: 'form', info: stage.info, busy: false }); }}
          />
        )}

        {stage.kind === 'form' && (
          <form onSubmit={onDetails} noValidate>
            <h1 style={S.title}>{t('join.title')}</h1>
            <p style={S.muted}>{t('join.subtitle', { label: stage.info.label })}</p>
            <div style={S.benefit}>
              <strong>{t('join.benefit', { plan: stage.info.planName, credits: stage.info.credits })}</strong>
              <span style={S.small}>{t('join.remaining', { n: stage.info.remaining })}</span>
            </div>

            <div style={S.row}>
              <Field label={t('join.firstName')} value={form.firstName} onChange={set('firstName')} invalid={invalid('firstName')} autoComplete="given-name" />
              <Field label={t('join.lastName')} value={form.lastName} onChange={set('lastName')} invalid={invalid('lastName')} autoComplete="family-name" />
            </div>
            <Field label={t('join.email')} type="email" value={form.email} onChange={set('email')} invalid={invalid('email')} autoComplete="email" hint={t('join.emailHint')} />
            <Field label={t('join.password')} type="password" value={form.password} onChange={set('password')} invalid={invalid('password')} autoComplete="new-password" hint={t('join.passwordHint', { n: PASSWORD_MIN_LENGTH })} />

            <label style={{ ...S.check, color: invalid('acceptTerms') ? '#f87171' : undefined }}>
              <input type="checkbox" checked={form.acceptTerms} onChange={(e) => set('acceptTerms')(e.target.checked)} />
              <span>{t('join.acceptTerms')}</span>
            </label>

            {error && <p style={S.error}>{t(error)}</p>}
            {badFields.length > 0 && !error && <p style={S.error}>{t('join.err.invalid')}</p>}

            <button type="submit" className="btn btn-glow" style={S.button} disabled={stage.busy}>
              {stage.busy ? t('join.sendingOtp') : t('join.sendOtp')}
            </button>
            <p style={S.small}>{t('join.haveAccount')} <a href="/dashboard" style={S.link}>{t('join.goLogin')}</a></p>
          </form>
        )}
      </div>
    </div>
  );
}

function toJoinError(err: unknown): { status: number; details?: { reason?: string; fields?: string[] } | null } {
  if (err instanceof ApiError) return { status: err.status, details: err.details ?? null };
  return { status: 0 };
}

interface FieldProps {
  label: string; value: string; onChange: (v: string) => void; invalid: boolean;
  type?: string; autoComplete?: string; hint?: string;
}
function Field({ label, value, onChange, invalid, type = 'text', autoComplete, hint }: FieldProps) {
  return (
    <label style={S.field}>
      <span style={S.label}>{label}</span>
      <input
        type={type} value={value} autoComplete={autoComplete} required
        onChange={(e) => onChange(e.target.value)}
        style={{ ...S.input, borderColor: invalid ? '#f87171' : 'var(--color-border)' }}
      />
      {hint && <span style={S.small}>{hint}</span>}
    </label>
  );
}

const S: Record<string, CSSProperties> = {
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '24px 16px', position: 'relative', zIndex: 1,
  },
  card: {
    width: '100%', maxWidth: '440px', background: 'var(--color-surface)',
    border: '1px solid var(--color-border)', borderRadius: '16px', padding: '28px 24px',
    display: 'flex', flexDirection: 'column', gap: '12px', backdropFilter: 'blur(12px)',
  },
  title: { margin: '4px 0 0', fontSize: '26px', fontWeight: 700, fontFamily: 'var(--font-display)', letterSpacing: '-0.02em', color: 'var(--color-text)' },
  muted: { margin: 0, color: 'var(--color-text-secondary)', fontSize: '15px', lineHeight: 1.5 },
  small: { color: 'var(--color-text-muted)', fontSize: '13px' },
  benefit: {
    display: 'flex', flexDirection: 'column', gap: '4px', padding: '12px 16px', borderRadius: '12px',
    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(139, 92, 246, 0.05))',
    border: '1px solid rgba(139, 92, 246, 0.3)', margin: '8px 0 12px', color: 'var(--color-text)',
  },
  row: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 160px', marginBottom: '12px' },
  label: { fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary)' },
  input: {
    padding: '12px 14px', borderRadius: '12px', border: '1px solid var(--color-border)',
    background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: '16px', outline: 'none',
    fontFamily: 'var(--font-body)',
  },
  check: { display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '14px', margin: '4px 0 16px', cursor: 'pointer', color: 'var(--color-text-secondary)' },
  error: { margin: '0 0 12px', color: '#f87171', fontSize: '14px' },
  button: { width: '100%', justifyContent: 'center' },
  link: { color: 'var(--gradient-purple-light)', textDecoration: 'none', fontWeight: 500 },
  linkButton: { background: 'none', border: 'none', padding: 0, color: 'var(--gradient-purple-light)', cursor: 'pointer', fontSize: '13px', fontFamily: 'var(--font-body)' },
};
